import {
    selectGroupWeight,
    selectIsFormWeighted,
    selectQuestionGroupName,
    selectQuestionGroup,
} from "features/qualityForm/qualityFormSlice";
import { selectFormType } from "features/qualityForm/qualityFormSlices/qualityFormCommons";
import { AppLocaleProvider } from "features/i18n/useLocale";
import {
    GuxTag,
    GuxFormFieldCheckbox,
    GuxTooltip,
    GuxTruncate,
} from "genesys-spark-components-react";
import { Decimal, Percentage } from "features/i18n/Percentage";
import {
    scorableSliceActions,
    selectGroupScore,
    selectIsScorableComplete,
    getPreviewScores,
    sendAnswersDataOnFormDataChange,
    isInitiallyHiddenOnPageLoadAndCanResetMarkedNA,
    selectAuthorizedActions,
    canEditScore,
} from "./scorableSlice";
import { useStrings } from "features/i18n/useStrings";
import strings from "./ScorableQuestionGroup.strings";
import "./ScorableQuestionGroup.less";
import { ScoreTag } from "features/common/ScoreTag";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { useContext, useEffect } from "react";
import {
    PreviewModeContext,
    HasEditScoreOrEditPermissionsContext,
} from "features/qualityForm/common/Contexts";
import { MarkdownInline } from "features/common/Markdown";
import { isNumberAndNotNaN } from "util/NumberInputOperator";
import FormType from "enums/FormType";

import { selectFeatureToggle } from "features/featureToggles/featureToggleSlice";
import FeatureToggle from "enums/FeatureToggle";
import TagAccent from "enums/TagAccent";

export function useWeightsEnabled() {
    return useAppSelector(selectIsFormWeighted);
}

export function QuestionGroupScore({
    score,
    critical,
}: {
    score: { total: number; max: number };
    critical?: boolean;
}) {
    const t = useStrings(strings);

    const percentage = score.total / score.max;
    return !isNaN(percentage) ? (
        <ScoreTag mode={critical ? "critical" : "default"}>
            <Percentage>{percentage}</Percentage>
            <GuxTooltip>
                {critical ? t.tooltip.critical(score) : t.tooltip.score(score)}
            </GuxTooltip>
        </ScoreTag>
    ) : (
        <></>
    );
}

interface IScore {
    total: number;
    max: number;
}

export function useGroupScore(
    id: string,
    weightsEnabled: boolean
): IScore | undefined {
    const {
        totalScore,
        maxTotalScore,
        totalScoreUnweighted,
        maxTotalScoreUnweighted,
    } = useAppSelector((state) => selectGroupScore(state, id)) ?? {};

    if (!weightsEnabled) {
        if (
            totalScoreUnweighted !== undefined &&
            maxTotalScoreUnweighted !== undefined
        ) {
            return {
                total: totalScoreUnweighted,
                max: maxTotalScoreUnweighted,
            };
        }
    } else {
        if (totalScore !== undefined && maxTotalScore !== undefined) {
            return {
                total: totalScore,
                max: maxTotalScore,
            };
        }
    }
}

export function useGroupCriticalScore(
    id: string,
    weightsEnabled: boolean
): IScore | undefined {
    const {
        totalCriticalScore,
        maxTotalCriticalScore,
        totalCriticalScoreUnweighted,
        maxTotalCriticalScoreUnweighted,
    } = useAppSelector((state) => selectGroupScore(state, id)) ?? {};

    if (!weightsEnabled) {
        if (
            totalCriticalScoreUnweighted !== undefined &&
            maxTotalCriticalScoreUnweighted !== undefined
        ) {
            return {
                total: totalCriticalScoreUnweighted,
                max: maxTotalCriticalScoreUnweighted,
            };
        }
    } else {
        if (
            totalCriticalScore !== undefined &&
            maxTotalCriticalScore !== undefined
        ) {
            return {
                total: totalCriticalScore,
                max: maxTotalCriticalScore,
            };
        }
    }
}

export function QuestionGroupScores({ id }: { id: string }) {
    const score = useGroupScore(id, false);
    const criticalScore = useGroupCriticalScore(id, false);
    return (
        <>
            {score && <QuestionGroupScore score={score} />}
            {criticalScore && (
                <QuestionGroupScore score={criticalScore} critical={true} />
            )}
        </>
    );
}

export function ScorableQuestionGroup({
    id,
    isRescoreMode,
}: {
    id: string;
    isRescoreMode: boolean;
}) {
    const t = useStrings(strings);
    const dispatch = useAppDispatch();
    const name = useAppSelector((state) => selectQuestionGroupName(state, id));
    const weightsEnabled = useWeightsEnabled();
    const weight = useAppSelector((state) => selectGroupWeight(state, id));
    const isScorableComplete =
        useAppSelector(selectIsScorableComplete) && !isRescoreMode;
    const isQuestionGroupNaEnabled = useAppSelector((state) => {
        return selectQuestionGroup(state, id)?.naEnabled ?? false;
    });
    const isQuestionGroupMarkedNa = useAppSelector((state) => {
        return selectGroupScore(state, id)?.markedNA ?? false;
    });

    // A rule of thumb: Don't use isQuestionGroupSystemMarkedNa without the FT check.
    const {
        isQuestionGroupSystemMarkedNa,
        canResetMarkedNABasedOnSystemMarkedNA,
    } = useAppSelector((state) => {
        const isSystemMarkedNaFeatureEnabled = selectFeatureToggle(
            state,
            FeatureToggle.SYSTEM_MARKED_NA_FEATURE_TOGGLE_NAME
        );

        const formType = selectFormType(state);

        // for Survey, this should always be false since it does not have any NA enabled groups
        let canResetMarkedNABasedOnSystemMarkedNA = false;

        if (formType === FormType.ASSESSMENT) {
            // When cleaning up the FT, set this variable to "true"
            canResetMarkedNABasedOnSystemMarkedNA =
                isSystemMarkedNaFeatureEnabled;
        } else if (formType === FormType.EVALUATION) {
            const authorizedActions = selectAuthorizedActions(state);
            // TODO: When cleaning up the FT, just remove FT check
            canResetMarkedNABasedOnSystemMarkedNA =
                isSystemMarkedNaFeatureEnabled &&
                canEditScore(authorizedActions);
        }

        // When the FT is off, this systemMarkedNA value will be false because of the default value of Java's primitive boolean variable.
        const isQuestionGroupSystemMarkedNa =
            selectGroupScore(state, id)?.systemMarkedNA ?? false;

        return {
            isQuestionGroupSystemMarkedNa,
            canResetMarkedNABasedOnSystemMarkedNA,
        };
    });
    const { previewMode } = useContext(PreviewModeContext);
    const { hasEditScoreOrEditPermissions } = useContext(
        HasEditScoreOrEditPermissionsContext
    );
    const formType = useAppSelector((state) => {
        return selectFormType(state);
    });

    const isAssessmentUIEnhancementsFeatureEnabled = useAppSelector((state) => {
        return (
            selectFeatureToggle(
                state,
                FeatureToggle.ASSESSMENT_UI_ENHANCEMENTS_TOGGLE
            ) && selectFeatureToggle(state, FeatureToggle.LEARNING_SCORM)
        );
    });

    const isFlareThemeEnabled = useAppSelector((state) =>
        selectFeatureToggle(state, FeatureToggle.SPARK_FLARE)
    );
    const tagAccent = isFlareThemeEnabled ? TagAccent.FLARE : TagAccent.LEGACY;

    useEffect(() => {
        if (
            isInitiallyHiddenOnPageLoadAndCanResetMarkedNA({
                isMarkedNA: isQuestionGroupMarkedNa,
                isSystemMarkedNA: isQuestionGroupSystemMarkedNa,
                canResetMarkedNABasedOnSystemMarkedNA,
            }) &&
            (formType === FormType.EVALUATION ||
                formType === FormType.ASSESSMENT)
        ) {
            dispatch(
                scorableSliceActions.setGroupNA({
                    questionGroupId: id,
                    isMarkedNA: false,
                })
            );
        }
    }, []);

    const shouldHideScoreForAssessment =
        isAssessmentUIEnhancementsFeatureEnabled &&
        formType === FormType.ASSESSMENT &&
        !previewMode;

    return (
        <div
            className="ScorableQuestionGroup"
            data-testid="scorable-question-group"
        >
            <GuxTruncate max-lines={3} className="name">
                <MarkdownInline
                    markdownInlineText={name ?? ""}
                ></MarkdownInline>
            </GuxTruncate>
            {isScorableComplete && !shouldHideScoreForAssessment && (
                <AppLocaleProvider>
                    <div className="score-info">
                        {/* Note: We cannot remove explicit null check here since typescript complains.*/}
                        {weightsEnabled &&
                            weight !== undefined &&
                            isNumberAndNotNaN(weight) && (
                                <GuxTag accent={tagAccent} emphasis="subtle">
                                    <i className="fa fa-balance-scale" />
                                    <GuxTooltip>{t.tooltip.scale}</GuxTooltip>
                                    <Decimal>{weight}</Decimal>
                                </GuxTag>
                            )}
                        <QuestionGroupScores id={id} />
                    </div>
                </AppLocaleProvider>
            )}
            {isQuestionGroupNaEnabled &&
                (!isScorableComplete || isQuestionGroupMarkedNa) && (
                    <GuxFormFieldCheckbox>
                        <input
                            slot="input"
                            type="checkbox"
                            name={t.labels.na}
                            disabled={
                                isScorableComplete ||
                                hasEditScoreOrEditPermissions === false
                            }
                            aria-disabled={
                                isScorableComplete ||
                                hasEditScoreOrEditPermissions === false
                            }
                            checked={isQuestionGroupMarkedNa}
                            onChange={() => {
                                const changeQuestionGroupNA = () => {
                                    dispatch(
                                        scorableSliceActions.changeGroupNA(id)
                                    );
                                };
                                dispatch(
                                    sendAnswersDataOnFormDataChange(
                                        changeQuestionGroupNA
                                    )
                                );
                                if (previewMode) {
                                    dispatch(
                                        getPreviewScores({
                                            isScorableFinished: false,
                                        })
                                    );
                                }
                            }}
                            className={isScorableComplete ? "" : "Clickable"}
                        />
                        <label
                            className={`${
                                isScorableComplete ||
                                hasEditScoreOrEditPermissions === false
                                    ? ""
                                    : "Clickable"
                            }`}
                            slot="label"
                        >
                            {t.labels.na}
                        </label>
                    </GuxFormFieldCheckbox>
                )}
        </div>
    );
}
